// src/services/orderService.ts - PHASE 1 COMPLÈTE
import api from './api';

export interface Order {
  id: number;
  user_id: number;
  total_price: number;
  status: string;
  created_at: string;
  delivery_address?: string;
  delivery_phone?: string;
  delivery_instructions?: string;
  payment_method?: string;
  items: OrderItem[];
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  farmer_total?: number;
}

export interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  quantity: number;
  price_per_unit: number;
  delivery_fee?: number;
  product?: {
    name: string;
    description?: string;
  };
}

export interface OrderStats {
  orders_by_status: Record<string, number>;
  total_sales: number;
  total_orders: number;
  recent_orders: number;
  pending_payments?: number; // ✅ NOUVEAU
}

export interface OrderStatusUpdate {
  status: string;
}

// ✅ NOUVEAU - Interface pour infos paiement farmer
export interface OrderPaymentInfo {
  order_id: number;
  total_amount: number;
  farmer_name: string;
  farmer_merchant_phone: string;
  farmer_phone?: string; // ✅ CORRECTION - Optionnel
  payment_instructions: string;
  order_status: string;
}

// ✅ NOUVEAU - Interface pour confirmation paiement
export interface PaymentConfirmation {
  amount_received: number;
  payment_method: string;
  payment_reference?: string;
  notes?: string;
}

// ✅ MODIFIÉ - Nouveaux statuts Phase 1
export const ORDER_STATUSES = {
  payment_pending: 'En attente de paiement',
  payment_confirmed: 'Paiement confirmé',
  preparing: 'En préparation', 
  ready: 'Prête à expédier',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
} as const;

// ✅ MODIFIÉ - Couleurs pour nouveaux statuts
export const ORDER_STATUS_COLORS = {
  payment_pending: 'bg-yellow-100 text-yellow-800',
  payment_confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-orange-100 text-orange-800',
  ready: 'bg-green-100 text-green-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-500 text-white',
  cancelled: 'bg-red-100 text-red-800'
} as const;

class OrderService {
  // ===== POUR LES FARMERS - Gestion des commandes reçues =====
  
  async getFarmerOrders(farmerId: number, status?: string): Promise<Order[]> {
    try {
      console.log('🔍 Récupération commandes farmer:', { farmerId, status });
      
      const params = status ? { status } : {};
      const response = await api.get(`/orders/farmer/${farmerId}`, { params });
      
      console.log('✅ Commandes récupérées:', response.data.length);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des commandes farmer:', error);
      throw error;
    }
  }

  async getFarmerOrderStats(farmerId: number): Promise<OrderStats> {
    try {
      console.log('📊 Récupération stats farmer:', farmerId);
      
      const response = await api.get(`/orders/farmer/${farmerId}/stats`);
      
      console.log('✅ Stats récupérées:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des stats farmer:', error);
      throw error;
    }
  }

  async getFarmerRecentOrders(farmerId: number, limit: number = 5): Promise<Order[]> {
    try {
      console.log('🕒 Récupération commandes récentes farmer:', { farmerId, limit });
      
      const response = await api.get(`/orders/farmer/${farmerId}/recent?limit=${limit}`);
      
      console.log('✅ Commandes récentes récupérées:', response.data.length);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des commandes récentes:', error);
      throw error;
    }
  }

  async getOrderFarmerDetails(orderId: number, farmerId: number): Promise<any> {
    try {
      console.log('🔍 Récupération détails commande:', { orderId, farmerId });
      
      const response = await api.get(`/orders/${orderId}/farmer-details/${farmerId}`);
      
      console.log('✅ Détails commande récupérés:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des détails commande:', error);
      throw error;
    }
  }

  async updateOrderStatus(orderId: number, farmerId: number, status: string): Promise<any> {
    try {
      console.log('🔧 Mise à jour statut:', { orderId, farmerId, status });
      
      const response = await api.put(`/orders/${orderId}/status?farmer_id=${farmerId}`, {
        status: status
      });
      
      console.log('✅ Statut mis à jour avec succès:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du statut:', error);
      
      if (error.response) {
        console.error('Détails erreur:', {
          status: error.response.status,
          data: error.response.data,
          url: error.config?.url,
          method: error.config?.method,
          sentData: error.config?.data
        });
      }
      
      throw error;
    }
  }

  // ✅ NOUVEAU - Récupérer commandes en attente de paiement pour farmer
  async getFarmerPendingPayments(farmerId: number): Promise<Order[]> {
    try {
      console.log('💰 Récupération commandes en attente de paiement:', farmerId);
      
      const response = await api.get(`/farmer/pending-payments`);
      
      console.log('✅ Commandes en attente récupérées:', response.data.length);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des commandes en attente:', error);
      throw error;
    }
  }

  // ✅ NOUVEAU - Confirmer réception paiement
// ✅ CORRIGER cette méthode dans votre orderService.ts
  async confirmPaymentReceived(orderId: number, confirmationData: PaymentConfirmation): Promise<any> {
    try {
      console.log('✅ Confirmation paiement:', { orderId, confirmationData });
      
      // ✅ CORRECTION - Utiliser l'endpoint farmer au lieu de orders
      const response = await api.post(`/farmer/confirm-payment/${orderId}`, confirmationData);
      
      console.log('✅ Paiement confirmé avec succès:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la confirmation de paiement:', error);
      throw error;
    }
  }

  // ===== POUR LES CUSTOMERS - Nouvelles méthodes Phase 1 =====
  
  // ✅ NOUVEAU - Récupérer infos de paiement farmer
  async getOrderPaymentInfo(orderId: number): Promise<OrderPaymentInfo> {
    try {
      console.log('💳 Récupération infos paiement farmer:', orderId);
      
      const response = await api.get(`/orders/${orderId}/payment-info`);
      
      console.log('✅ Infos paiement récupérées:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des infos de paiement:', error);
      throw error;
    }
  }

  // ✅ NOUVEAU - Vérifier statut de paiement
  async checkOrderPaymentStatus(orderId: number): Promise<any> {
    try {
      console.log('🔍 Vérification statut paiement:', orderId);
      
      const response = await api.get(`/orders/${orderId}/payment-status`);
      
      console.log('✅ Statut paiement récupéré:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut:', error);
      throw error;
    }
  }

  // ===== MÉTHODES EXISTANTES =====
  
  async getCustomerOrders(userId: number): Promise<Order[]> {
    try {
      const response = await api.get(`/orders/user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw error;
    }
  }

  async getOrderDetails(orderId: number): Promise<Order> {
    try {
      const response = await api.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  }

  // ===== UTILITAIRES =====

  getStatusLabel(status: string): string {
    return ORDER_STATUSES[status as keyof typeof ORDER_STATUSES] || status;
  }

  getStatusColor(status: string): string {
    return ORDER_STATUS_COLORS[status as keyof typeof ORDER_STATUS_COLORS] || 'bg-gray-100 text-gray-800';
  }

  // ✅ MODIFIÉ - Transitions logiques Phase 1
  getNextValidStatuses(currentStatus: string): string[] {
    const statusFlow = {
      payment_pending: ['payment_confirmed', 'cancelled'],
      payment_confirmed: ['preparing', 'cancelled'],
      preparing: ['ready', 'cancelled'],
      ready: ['shipped'],
      shipped: ['delivered'],
      delivered: [],
      cancelled: []
    };

    return statusFlow[currentStatus as keyof typeof statusFlow] || [];
  }

  formatPrice(price: number): string {
    return `${price.toLocaleString()} FCFA`;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // ✅ NOUVEAU - Utilitaires Phase 1
  
  isPaymentPending(status: string): boolean {
    return status === 'payment_pending';
  }

  canConfirmPayment(status: string): boolean {
    return status === 'payment_pending';
  }

  // Ajouter cette méthode dans la classe OrderService
async confirmPayment(orderId: number, farmerId: number): Promise<any> {
  try {
    console.log('💰 Confirmation paiement commande:', { orderId, farmerId });
    
    const response = await api.post(`/orders/${orderId}/confirm-payment`, {
      farmer_id: farmerId
    });
    
    console.log('✅ Paiement confirmé:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Erreur confirmation paiement:', error);
    throw error;
  }
}

  getStatusBadgeClass(status: string): string {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    const colorClass = this.getStatusColor(status);
    return `${baseClasses} ${colorClass}`;
  }

  formatPaymentMethod(method: string): string {
    const methods = {
      'mobile_money': 'Mobile Money',
      'cash': 'Paiement à la livraison',
      'card': 'Carte bancaire'
    };
    return methods[method as keyof typeof methods] || method;
  }
}



export default new OrderService();